import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import { PROJECT_LAYER_LIST_QUERY_KEYS } from "components/project/layers/queries";

import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS
} from "../../queries";

export interface BulkDeleteShapefilesRequest {
  projectId: string;
  shapefileIds: string[];
  projectShapefileIds: string[];
  folderIds: string[];
  orgFolderIds: string[];
  systemFolderIds: string[];
}

export type ProtectedItem = { id: string; name: string; reason: string };
export type ProtectedItems = {
  shapefiles: ProtectedItem[];
  folders: ProtectedItem[];
};

export interface BulkDeleteShapefilesResponse {
  deletedShapefiles: number;
  deletedFolders: number;
  status: string;
  protectedItems?: ProtectedItems;
}

export type BulkDeleteShapefilesParams = Omit<BulkDeleteShapefilesRequest, "projectId">;

export type BulkDeleteAxiosError = Error & {
  response?: {
    data?: {
      protectedItems?: ProtectedItems;
    };
  };
};

interface IUseDeleteShapefileListMutationParams {
  onError?: (error: BulkDeleteAxiosError, variables: BulkDeleteShapefilesParams) => void;
  onMutate?: () => void;
  onSettled?: (
    data?: BulkDeleteShapefilesResponse,
    error?: BulkDeleteAxiosError,
    variables?: BulkDeleteShapefilesParams,
    context?: unknown
  ) => void;
  onSuccess?: (
    data: BulkDeleteShapefilesResponse,
    variables: BulkDeleteShapefilesParams
  ) => void;
  projectId: string;
}

export async function bulkDeleteShapefiles(
  params: BulkDeleteShapefilesRequest
): Promise<BulkDeleteShapefilesResponse> {
  const response = await axios.post(
    `${projectServiceEndpoint}/shapefile/bulk-delete`,
    params
  );
  return response.data;
}

/**
 * Hook for deleting multiple shapefiles and folders from a project.
 * This will handle both project-linked shapefiles and folders.
 */
export function useDeleteShapefileListMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess,
  projectId
}: IUseDeleteShapefileListMutationParams) {
  const queryClient = useQueryClient();

  return useMutation<
    BulkDeleteShapefilesResponse,
    BulkDeleteAxiosError,
    BulkDeleteShapefilesParams
  >(
    (params: BulkDeleteShapefilesParams) =>
      bulkDeleteShapefiles({ ...params, projectId }),
    {
      onMutate,
      onSuccess: (data, variables) => {
        // Invalidate queries after successful deletion
        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
        });
        queryClient.invalidateQueries({
          queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList, projectId]
        });
        queryClient.invalidateQueries({
          queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
        });

        onSuccess && onSuccess(data, variables);
      },
      onError: (error, variables) => {
        onError && onError(error, variables);
      },
      onSettled: onSettled && onSettled
    }
  );
}
